import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import * as Icons from "lucide-react";
import { ArrowUpRight, ArrowRight, PhoneCall } from "lucide-react";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { Overline, CTABand } from "@/components/Bits";
import Accreditations from "@/components/Accreditations";
import logoMark from "@/assets/logo-mark.png";
import {
  CORE_SERVICES,
  STATS,
  DESTINATIONS,
  HERO_IMG,
} from "@/lib/site";

const HERO_LINES = ["Seamless Travel", "for Smart Businesses"];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div data-testid="home-page">
      {/* ===================== HERO ===================== */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden flex items-end">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Business class cabin seat with window" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue/85 via-brand-blue/55 to-brand-green/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/90 via-transparent to-transparent" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 sm:px-8 pb-20 pt-36">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            {/* Brand lockup: translucent frosted box to highlight the wordmark */}
            <div className="inline-flex items-center gap-4 md:gap-5 rounded-2xl bg-white/35 backdrop-blur-md px-5 py-3.5 md:px-7 md:py-5 ring-1 ring-white/40 shadow-xl shadow-brand-blue/10">
              <div className="leading-none">
                <span className="font-heading font-extrabold tracking-tight text-5xl md:text-7xl text-[#1B4F9C]">
                  FlyGowell
                </span>
                <span className="block mt-2 font-heading font-semibold text-[#1B4F9C] uppercase tracking-[0.32em] text-sm md:text-xl">
                  Travel Solutions
                </span>
              </div>
              <img
                src={logoMark}
                alt="Fly Gowell logo"
                className="h-16 md:h-24 w-auto object-contain"
              />
            </div>
          </motion.div>

          <h1 className="mt-6 font-heading text-[11vw] leading-[0.98] sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-7 max-w-2xl text-lg md:text-xl text-white/85 leading-relaxed"
          >
            End-to-end corporate travel covering ticketing, visas, documentation and
            executive support, all handled with precision.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-callback-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-8 py-4 text-base font-semibold text-white transition-all hover:gap-3 hover:brightness-110"
            >
              <PhoneCall size={18} /> Schedule a Callback
            </Link>
            <Link
              to="/contact"
              data-testid="hero-touch-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/60 px-8 py-4 text-base font-semibold text-white transition-all hover:bg-white hover:text-brand-blue"
            >
              Get in Touch <ArrowUpRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===================== WHO WE ARE ===================== */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <Overline>Who We Are</Overline>
              <div className="mt-4 h-1 w-16 bg-gradient-to-r from-brand-blue to-brand-green rounded-full" />
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="text-2xl md:text-3xl font-heading font-medium text-brand-blue leading-snug tracking-tight">
                Fly Gowell Travel Solutions specializes in managing seamless, end-to-end travel
                for corporate clients and business executives.
              </p>
              <p className="mt-6 text-lg text-brand-slate leading-relaxed max-w-3xl">
                From international ticketing and visa assistance to tailored itineraries and
                documentation support, we ensure every journey is efficient, reliable, and
                stress-free.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== CORE SERVICES ===================== */}
      <section className="bg-white border-y border-brand-blue/5">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <Overline>What We Do</Overline>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-brand-blue max-w-xl">
                Core services, engineered for business
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/corporate-services" className="inline-flex items-center gap-2 text-brand-ocean font-medium hover:gap-3 transition-all">
                Explore all services <ArrowRight size={18} />
              </Link>
            </Reveal>
          </div>

          <RevealGroup className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CORE_SERVICES.map((s) => {
              const Icon = Icons[s.icon] || Icons.Circle;
              return (
                <RevealItem key={s.title}>
                  <Link
                    to="/contact"
                    data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group block h-full rounded-2xl border border-brand-blue/8 bg-brand-bg p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-brand-green/30 cursor-pointer"
                  >
                    <div className="inline-flex rounded-xl bg-gradient-to-br from-brand-blue to-brand-ocean p-3.5 text-white shadow-sm transition-transform group-hover:scale-110">
                      <Icon size={24} />
                    </div>
                    <h3 className="mt-6 text-xl font-heading font-semibold text-brand-blue">{s.title}</h3>
                    <p className="mt-3 text-brand-slate leading-relaxed text-[15px]">{s.desc}</p>
                  </Link>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* ===================== DESTINATIONS STRIP ===================== */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
        <Reveal>
          <Overline>Global Reach</Overline>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-brand-blue max-w-2xl">
            Destinations we move executives to, every day
          </h2>
        </Reveal>
        <RevealGroup className="mt-14 grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {DESTINATIONS.map((d) => (
            <RevealItem key={d.name}>
              <Link to="/contact" data-testid={`destination-${d.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group relative block h-72 overflow-hidden rounded-2xl cursor-pointer">
                <img
                  src={d.img}
                  alt={d.name}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/85 via-brand-blue/10 to-transparent" />
                <div className="absolute bottom-0 left-0 p-5">
                  <p className="text-2xl font-heading font-semibold text-white">{d.name}</p>
                </div>
              </Link>
            </RevealItem>
          ))}
        </RevealGroup>
      </section>

      {/* ===================== STATS MARQUEE ===================== */}
      <section className="border-y border-brand-blue/5 bg-white py-14">
        <Marquee speed={45} gradient={false} pauseOnHover>
          {STATS.concat(STATS).map((s, i) => (
            <div key={i} className="mx-10 flex items-baseline gap-4">
              <span className="font-heading text-5xl md:text-7xl font-extrabold text-brand-blue">
                {s.value}
              </span>
              <span className="font-heading text-3xl md:text-5xl font-extrabold text-outline">
                {s.label}
              </span>
              <span className="mx-6 text-brand-green text-4xl">•</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* ===================== WHY TEASER ===================== */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
        <div className="grid gap-8 md:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <Link to="/contact" className="block rounded-2xl border border-brand-blue/8 p-8 h-full bg-brand-bg transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-brand-green/30 cursor-pointer">
                <p className="font-heading text-4xl font-bold text-brand-green">{s.value}</p>
                <p className="mt-2 text-brand-blue font-medium">{s.label}</p>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 flex justify-center">
            <Link to="/why-fly-gowell" className="inline-flex items-center gap-2 text-brand-ocean font-medium hover:gap-3 transition-all">
              Why choose Fly Gowell <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-brand-blue/5 bg-white py-16">
        <Accreditations />
      </section>

      <CTABand />
    </div>
  );
}
